import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api/v1';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur de requête pour ajouter le token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Remove Content-Type for FormData to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Normalise les URLs storage relatives en URLs absolues
// Appelé récursivement sur tout objet de réponse API
const STORAGE_URL_BASE = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api/v1')
  .replace('/v1', '');

const URL_FIELDS = ['photo_url', 'profile_picture', 'cover_photo', 'avatar', 'thumbnail', 'media_url', 'media_file', 'url', 'thumbnail_url', 'image_url', 'video_url', 'story_url'];
const DEFAULT_AVATARS = ['default-avatar.png', 'default-avatar.svg', 'default.png'];

const PRODUCTION_HOST = 'https://chatoo.me';

function normalizeStorageUrls(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    obj.forEach(normalizeStorageUrls);
    return obj;
  }
  for (const key of Object.keys(obj)) {
    if (URL_FIELDS.includes(key) && typeof obj[key] === 'string') {
      const val = obj[key];
      if (!val || DEFAULT_AVATARS.includes(val)) {
        obj[key] = null;
      } else if (val.startsWith('http://127.0.0.1') || val.startsWith('http://localhost')) {
        // URL localhost → remplacer par l'URL de production
        obj[key] = val
          .replace(/^http:\/\/127\.0\.0\.1:\d+/, STORAGE_URL_BASE)
          .replace(/^http:\/\/localhost:\d+/, STORAGE_URL_BASE)
          .replace(/^http:\/\/localhost/, STORAGE_URL_BASE);
      } else if (val.startsWith(PRODUCTION_HOST + '/storage/')) {
        // URL production sans /api/ → ajouter /api/
        obj[key] = val.replace(PRODUCTION_HOST + '/storage/', PRODUCTION_HOST + '/api/storage/');
      } else if (!val.startsWith('http')) {
        // Chemin relatif → préfixer avec la base API
        const clean = val.startsWith('/') ? val : '/' + val;
        obj[key] = STORAGE_URL_BASE + clean;
      }
    } else if (obj[key] && typeof obj[key] === 'object') {
      normalizeStorageUrls(obj[key]);
    }
  }
  return obj;
}

// Intercepteur de réponse pour gérer les erreurs
api.interceptors.response.use(
  (response) => {
    // Normaliser toutes les URLs storage dans la réponse
    if (response.data) {
      normalizeStorageUrls(response.data);
    }
    return response;
  },
  (error) => {
    // Si erreur 401 sur une route protégée, déconnecter l'utilisateur
    // Ne pas rediriger si c'est la requête de login elle-même (mauvais identifiants)
    if (error.response?.status === 401 && !error.config?.url?.includes('/login')) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
    }

    // Afficher les erreurs avec toast
    if (error.response) {
      const message = error.response.data?.message ||
                     error.response.data?.detail ||
                     'Une erreur est survenue';
      toast.error(message);
    } else if (error.request) {
      toast.error('Erreur de connexion au serveur');
    } else {
      toast.error('Une erreur est survenue');
    }

    return Promise.reject(error);
  }
);

// Admin API methods
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getLogs: (params) => api.get('/admin/logs', { params }),
  deleteLog: (logId) => api.delete(`/admin/logs/${logId}`),
  getReports: (params) => api.get('/admin/reports', { params }),
  updateReportStatus: (reportId, data) => api.patch(`/admin/reports/${reportId}/status`, data),
  getAnnouncements: () => api.get('/admin/announcements'),

  // User management
  getUsers: (params) => api.get('/admin/users', { params }),
  updateUser: (userId, data) => api.patch(`/admin/users/${userId}`, data),
  suspendUser: (userId, data) => api.post(`/admin/users/${userId}/suspend`, data),
  banUser: (userId, data) => api.post(`/admin/users/${userId}/ban`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  activateUser: (userId) => api.post(`/admin/users/${userId}/activate`),

  // Diamant purchase management
  getDiamantTransactions: (params) => api.get('/admin/diamant/transactions', { params }),
};

// Certification API methods
export const certificationAPI = {
  // User methods
  getStatus: () => api.get('/certifications/status'),
  getHistory: () => api.get('/certifications/history'),
  request: (formData) => api.post('/certifications/request', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  viewDocument: (certificationId) => api.get(`/certifications/${certificationId}/document`, {
    responseType: 'blob'
  }),
  
  // Admin methods
  getAll: (params) => api.get('/admin/certifications', { params }),
  approve: (certificationId, data) => api.post(`/admin/certifications/${certificationId}/approve`, data),
  reject: (certificationId, data) => api.post(`/admin/certifications/${certificationId}/reject`, data),
};


// Admin Discussion Management API methods
export const adminDiscussionAPI = {
  // Get all discussions with filters
  getAllDiscussions: (params) => api.get('/admin/discussions', { params }),
  
  // Get statistics
  getStats: (params) => api.get('/admin/discussions/stats', { params }),
  
  // Get users for impersonation
  getUsersForImpersonation: (params) => api.get('/admin/discussions/users-for-impersonation', { params }),
  
  // Get messages from a specific conversation
  getMessages: (conversationId, params) => api.get(`/admin/discussions/${conversationId}/messages`, { params }),
  
  // Send message as a user
  sendMessage: (conversationId, data) => api.post(`/admin/discussions/${conversationId}/messages`, data),
  
  // Upload media as a user
  uploadMedia: (conversationId, formData) => api.post(`/admin/discussions/${conversationId}/messages/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Mark messages as read for a user
  markAsRead: (conversationId, data) => api.post(`/admin/discussions/${conversationId}/mark-read`, data),
  
  // Delete a conversation (superadmin only)
  deleteDiscussion: (conversationId) => api.delete(`/admin/discussions/${conversationId}`),
};

// Dashboard API methods
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getActivity: (params) => api.get('/dashboard/activity', { params }),
};

export default api;
